import { numFont } from '../../../utils/fonts';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography, Stack } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TodayIcon from '@mui/icons-material/Today';
import BuildIcon from '@mui/icons-material/Build';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useUserStore } from '../../../store';
import {
  useTimeclockStore,
  selectDaySession,
  selectWoSession,
  selectBreakSession,
  useDayPresenceSeconds,
} from '../../../storeTimeclock';
import { useClockController } from './useClockController';
import WorkOrderPicker from './WorkOrderPicker';
import { TOKENS, Plate, LiveDot, hms, hmShort, hhmm, useNow, elapsedSeconds, fmtDate } from './worktimeLib';

const TINY_GAP_MS = 60 * 1000; // ignore sub-minute gaps (rapid WO switches)

export default function ClockPage() {
  const { t } = useTranslation();
  const fetchActive = useTimeclockStore((s) => s.fetchActive);
  const fetchDay = useTimeclockStore((s) => s.fetchDay);
  const fetchConfig = useTimeclockStore((s) => s.fetchConfig);
  const fetchPending = useTimeclockStore((s) => s.fetchPending);
  const active = useTimeclockStore((s) => s.active);
  const day = useTimeclockStore((s) => s.day);
  const config = useTimeclockStore((s) => s.config);
  const pending = useTimeclockStore((s) => s.pending);
  // employee_id === null → confirmed unlinked user (from /users/me); undefined (older payload)
  // deliberately counts as linked so a stale bundle/backend never locks a real user out.
  const currentOrganization = useUserStore((s) => s.currentOrganization);
  const noEmployee = currentOrganization?.employee_id === null;
  const daySession = useTimeclockStore(selectDaySession);
  const woSession = useTimeclockStore(selectWoSession);
  const breakSession = useTimeclockStore(selectBreakSession);

  const ctrl = useClockController();
  const navigate = useNavigate();
  const nowMs = useNow();
  const [pickerOpen, setPickerOpen] = useState(false);
  const breakSec = breakSession ? elapsedSeconds(breakSession.started_at, nowMs) : 0;

  const track = config?.track || 'both';
  const hasDay = track !== 'work_order';
  const hasWo = track !== 'day';

  useEffect(() => {
    if (noEmployee) return;
    fetchConfig();
    fetchActive();
    fetchDay();
    fetchPending();
  }, [noEmployee, fetchConfig, fetchActive, fetchDay, fetchPending]);

  const activeKey = active.map((s) => `${s.id}:${s.ended_at || ''}`).join(',');
  useEffect(() => {
    if (noEmployee) return;
    fetchDay();
    fetchPending();
  }, [noEmployee, activeKey, fetchDay, fetchPending]);

  // Ending the work day routes to the confirmation screen unless the org auto-commits (§10).
  const endDay = async () => {
    if (!daySession) return;
    await ctrl.clockOutSession(daySession.id);
    if ((config?.handling || 'confirm') !== 'auto') {
      navigate('/time-clock/confirm');
    }
  };

  // Today's cumulative presence — centralized so every clock surface agrees (see storeTimeclock).
  const daySec = useDayPresenceSeconds(nowMs);
  const woSec = woSession ? elapsedSeconds(woSession.started_at, nowMs) : 0;

  // Today's first clock-in (the work day's real start), for the "Aloitettu klo" label.
  const dayStartIso = useMemo(() => {
    const starts = (day?.sessions || [])
      .filter((s) => s.kind === 'day')
      .map((s) => s.started_at)
      .filter(Boolean);
    if (daySession?.started_at) starts.push(daySession.started_at);
    return starts.length ? starts.reduce((a, b) => (new Date(a) <= new Date(b) ? a : b)) : daySession?.started_at;
  }, [day, daySession]);

  // "Tänään tähän mennessä" timeline: every closed WO segment PLUS the unassigned gaps inside the
  // presence span (present, no WO running → "muu työ"), in time order — so unmarked work time is
  // visible, not hidden until reconciliation. Gaps shorter than 1 min (rapid WO switches) are
  // dropped as noise. A running WO is shown in the card above, not duplicated here, but it still
  // covers its interval so no false gap appears under it.
  const timeline = useMemo(() => {
    const sessions = day?.sessions || [];
    const ms = (iso) => new Date(iso).getTime();
    const daySpans = sessions
      .filter((s) => s.kind === 'day')
      .map((s) => ({ start: ms(s.started_at), end: s.ended_at ? ms(s.ended_at) : nowMs }))
      .filter((s) => s.end > s.start)
      .sort((a, b) => a.start - b.start);
    // Covered = WO segments + break segments; the rest of the day span is Muu työ (other).
    const covered = sessions
      .filter((s) => s.kind === 'work_order' || s.kind === 'break')
      .map((s) => ({
        start: ms(s.started_at),
        end: s.ended_at ? ms(s.ended_at) : nowMs,
        type: s.kind === 'break' ? 'break' : 'wo',
        reg: s.maintenance_info?.vehicle_registration || '',
        running: !s.ended_at,
        id: s.id,
      }));

    const rows = [];
    for (const span of daySpans) {
      const segs = covered
        .map((w) => ({ ...w, start: Math.max(w.start, span.start), end: Math.min(w.end, span.end) }))
        .filter((w) => w.end > w.start)
        .sort((a, b) => a.start - b.start);
      let cursor = span.start;
      for (const w of segs) {
        if (w.start - cursor >= TINY_GAP_MS) {
          rows.push({ key: `o${cursor}`, type: 'other', start: cursor, end: w.start });
        }
        if (!w.running) rows.push({ key: `${w.type}${w.id}`, type: w.type, start: w.start, end: w.end, reg: w.reg });
        cursor = Math.max(cursor, w.end);
      }
      if (span.end - cursor >= TINY_GAP_MS) {
        rows.push({ key: `o${cursor}`, type: 'other', start: cursor, end: span.end, running: span.end === nowMs });
      }
    }
    return rows;
  }, [day, nowMs]);

  const onPick = (m) => {
    setPickerOpen(false);
    if (woSession && woSession.maintenance !== m.id) {
      ctrl.requestSwitch(m.id, {
        fromReg: woSession.maintenance_info?.vehicle_registration,
        toReg: m.vehicle?.registration_number,
        fromMaintenanceId: woSession.maintenance,
      });
    } else {
      ctrl.startWo(m.id);
    }
  };

  let body;
  if (noEmployee) {
    body = (
      <Box sx={{ textAlign: 'center', py: 6, color: TOKENS.muted }} data-testid="clock-no-employee">
        <ScheduleIcon sx={{ fontSize: 56, color: TOKENS.subtle, mb: 1 }} />
        <Typography sx={{ fontWeight: 700, color: TOKENS.fg }}>{t('worktime.noEmployee.title')}</Typography>
        <Typography sx={{ fontSize: 13 }}>{t('worktime.noEmployee.body')}</Typography>
      </Box>
    );
  } else if (!daySession && !woSession && !breakSession) {
    // IDLE
    body = (
      <Stack spacing={3} alignItems="center" sx={{ py: 4 }} data-testid="clock-idle">
        <Box
          sx={{
            width: 120,
            height: 120,
            borderRadius: '50%',
            backgroundColor: '#fff',
            border: `2px solid ${TOKENS.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: TOKENS.subtle,
          }}
        >
          <ScheduleIcon sx={{ fontSize: 56 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, color: TOKENS.fg }}>{t('worktime.idle.title')}</Typography>
        <Button
          fullWidth
          size="large"
          startIcon={<PlayArrowIcon />}
          onClick={hasDay ? ctrl.clockInDay : () => setPickerOpen(true)}
          disabled={ctrl.busy}
          sx={{ maxWidth: 320, py: 1.5, backgroundColor: TOKENS.green, color: '#fff', '&:hover': { backgroundColor: TOKENS.greenDark } }}
        >
          {hasDay ? t('worktime.idle.startDay') : t('worktime.idle.startWo')}
        </Button>
        {hasDay && hasWo && (
          <Typography sx={{ fontSize: 12, color: TOKENS.subtle, textAlign: 'center', maxWidth: 280 }}>{t('worktime.idle.hint')}</Typography>
        )}
      </Stack>
    );
  } else if (woSession) {
    // ON WORK ORDER
    body = (
      <Stack spacing={1.75} data-testid="clock-on-wo">
        <Box
          onClick={() => woSession.maintenance && navigate(`/maintenances/${woSession.maintenance}`)}
          sx={{
            background: `linear-gradient(135deg, ${TOKENS.green} 0%, ${TOKENS.greenDark} 100%)`,
            borderRadius: 3,
            p: 2.5,
            color: '#fff',
            cursor: woSession.maintenance ? 'pointer' : 'default',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              <LiveDot color="#fff" /> {t('worktime.onWo.running')}
            </Box>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
              <Plate size={15} color="#fff">
                {woSession.maintenance_info?.vehicle_registration || '—'}
              </Plate>
              {woSession.maintenance && <OpenInNewIcon sx={{ fontSize: 16, opacity: 0.85 }} />}
            </Box>
          </Box>
          <Typography sx={{ ...numFont, fontSize: 44, fontWeight: 700, letterSpacing: '-1px' }}>{hms(woSec)}</Typography>
          <Typography sx={{ fontSize: 13, opacity: 0.92 }} noWrap>
            {woSession.maintenance_info?.description || ''}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.25 }}>
          <Button fullWidth startIcon={<RestaurantIcon />} onClick={ctrl.startBreak} disabled={ctrl.busy} sx={{ backgroundColor: TOKENS.amber, color: '#fff', '&:hover': { backgroundColor: TOKENS.amberText } }}>
            {t('worktime.onWo.break')}
          </Button>
          <Button fullWidth variant="outlined" startIcon={<SwapHorizIcon />} onClick={() => setPickerOpen(true)} disabled={ctrl.busy}>
            {t('worktime.onWo.switch')}
          </Button>
        </Box>
        <Button fullWidth variant="outlined" startIcon={<CheckCircleIcon />} onClick={() => ctrl.clockOutSession(woSession.id)} disabled={ctrl.busy} sx={{ color: TOKENS.red, borderColor: TOKENS.redBorder }}>
          {t('worktime.onWo.endThis')}
        </Button>

        {hasDay && daySession && (
          <Box sx={{ backgroundColor: '#fff', border: `1px solid ${TOKENS.border}`, borderRadius: 2, p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, fontSize: 13, color: TOKENS.muted }}>
              <TodayIcon sx={{ fontSize: 16, color: TOKENS.accent }} /> {t('worktime.dayTotal')}
            </Box>
            <Box sx={{ ...numFont, fontSize: 15, fontWeight: 700, color: TOKENS.fg }}>{hms(daySec)}</Box>
          </Box>
        )}

        {timeline.length > 0 && <SegmentList rows={timeline} t={t} />}
      </Stack>
    );
  } else if (breakSession) {
    // ON MEAL BREAK
    body = (
      <Stack spacing={2} data-testid="clock-break">
        <Box sx={{ backgroundColor: TOKENS.amber, borderRadius: 3, p: 2.75, color: '#fff', textAlign: 'center' }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, fontSize: 12, fontWeight: 600, opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            <RestaurantIcon sx={{ fontSize: 16 }} /> {t('worktime.onBreak.running')}
          </Box>
          <Typography sx={{ ...numFont, fontSize: 46, fontWeight: 700, letterSpacing: '-1px', mt: 1 }}>{hms(breakSec)}</Typography>
        </Box>

        <Button fullWidth size="large" startIcon={<CheckCircleIcon />} onClick={() => ctrl.clockOutSession(breakSession.id)} disabled={ctrl.busy} sx={{ py: 1.5, backgroundColor: TOKENS.green, color: '#fff', '&:hover': { backgroundColor: TOKENS.greenDark } }}>
          {t('worktime.onBreak.end')}
        </Button>
        {hasWo && (
          <Button fullWidth variant="outlined" startIcon={<PlayArrowIcon />} onClick={() => setPickerOpen(true)} disabled={ctrl.busy}>
            {t('worktime.day.startWo')}
          </Button>
        )}

        {hasDay && daySession && (
          <Box sx={{ backgroundColor: '#fff', border: `1px solid ${TOKENS.border}`, borderRadius: 2, p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, fontSize: 13, color: TOKENS.muted }}>
              <TodayIcon sx={{ fontSize: 16, color: TOKENS.accent }} /> {t('worktime.dayTotal')}
            </Box>
            <Box sx={{ ...numFont, fontSize: 15, fontWeight: 700, color: TOKENS.fg }}>{hms(daySec)}</Box>
          </Box>
        )}

        {timeline.length > 0 && <SegmentList rows={timeline} t={t} />}
      </Stack>
    );
  } else {
    // DAY RUNNING, NO WORK ORDER
    body = (
      <Stack spacing={2} data-testid="clock-day">
        <Box sx={{ backgroundColor: TOKENS.navy, borderRadius: 3, p: 2.75, color: '#fff', textAlign: 'center' }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, fontSize: 12, fontWeight: 600, opacity: 0.85, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            <LiveDot color="#fff" /> {t('worktime.day.running')}
          </Box>
          <Typography sx={{ ...numFont, fontSize: 46, fontWeight: 700, letterSpacing: '-1px', mt: 1 }}>{hms(daySec)}</Typography>
          <Typography sx={{ fontSize: 12, opacity: 0.7 }}>{t('worktime.day.startedAt', { time: hhmm(dayStartIso) })}</Typography>
        </Box>

        {hasWo && (
          <>
            <Box sx={{ backgroundColor: TOKENS.amberSoft, border: `1px solid rgba(217,119,6,0.2)`, borderRadius: 2, p: 1.5, display: 'flex', gap: 1.25, alignItems: 'center' }}>
              <InfoOutlinedIcon sx={{ color: TOKENS.amber, fontSize: 18 }} />
              <Typography sx={{ fontSize: 13, color: TOKENS.amberText }}>{t('worktime.day.noWoNotice')}</Typography>
            </Box>
            <Button fullWidth size="large" startIcon={<PlayArrowIcon />} onClick={() => setPickerOpen(true)} disabled={ctrl.busy} sx={{ py: 1.5, backgroundColor: TOKENS.green, color: '#fff', '&:hover': { backgroundColor: TOKENS.greenDark } }}>
              {t('worktime.day.startWo')}
            </Button>
          </>
        )}

        <Button fullWidth variant="outlined" startIcon={<RestaurantIcon />} onClick={ctrl.startBreak} disabled={ctrl.busy} sx={{ color: TOKENS.amberText, borderColor: TOKENS.amber }}>
          {t('worktime.onBreak.start')}
        </Button>

        {timeline.length > 0 && <SegmentList rows={timeline} t={t} />}

        {hasDay && (
          <Button fullWidth variant="outlined" startIcon={<StopCircleIcon />} onClick={endDay} disabled={ctrl.busy} sx={{ color: TOKENS.red, borderColor: TOKENS.redBorder }}>
            {t('worktime.day.endDay')}
          </Button>
        )}
      </Stack>
    );
  }

  return (
    <Box sx={{ maxWidth: 480, mx: 'auto', p: { xs: 2, sm: 2.5 }, minHeight: '70vh' }} data-testid="clock-page">
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        {t('worktime.title')}
      </Typography>

      {/* Unconfirmed work days — the standing entry point into the reconcile screen. */}
      {(pending || []).length > 0 && (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {pending.map((d) => (
            <Box
              key={d}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                p: 1.25,
                borderRadius: 2,
                border: `1px solid rgba(217,119,6,0.25)`,
                backgroundColor: TOKENS.amberSoft,
              }}
            >
              <EventAvailableIcon sx={{ fontSize: 18, color: TOKENS.amber }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: TOKENS.amberText }}>
                  {t('worktime.reconcile.pendingTitle')}
                </Typography>
                <Typography sx={{ fontSize: 12, color: TOKENS.amberText, ...numFont }}>{fmtDate(d)}</Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                onClick={() => navigate(`/time-clock/confirm?date=${d}`)}
                sx={{ backgroundColor: TOKENS.amber, '&:hover': { backgroundColor: TOKENS.amberText } }}
              >
                {t('worktime.reconcile.confirm')}
              </Button>
            </Box>
          ))}
        </Stack>
      )}

      {body}
      {ctrl.dialogs}
      <WorkOrderPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={onPick} excludeMaintenanceId={woSession?.maintenance} />
    </Box>
  );
}

function SegmentList({ rows, t }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: TOKENS.muted, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
        {t('worktime.todaySoFar')}
      </Typography>
      <Stack spacing={0.75}>
        {rows.map((r) => {
          const isOther = r.type === 'other';
          const isBreak = r.type === 'break';
          const tinted = isOther || isBreak;
          return (
            <Box
              key={r.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                p: 1.25,
                backgroundColor: tinted ? TOKENS.amberSoft : TOKENS.bg,
                borderRadius: 1.5,
              }}
            >
              {isBreak ? (
                <RestaurantIcon sx={{ fontSize: 16, color: TOKENS.amber }} />
              ) : isOther ? (
                <MoreHorizIcon sx={{ fontSize: 16, color: TOKENS.amber }} />
              ) : (
                <BuildIcon sx={{ fontSize: 16, color: TOKENS.accent }} />
              )}
              <Box sx={{ flex: 1, ...numFont, fontSize: 12, color: TOKENS.fg }}>
                {hhmm(r.start)}–{hhmm(r.end)}
              </Box>
              <Box sx={{ fontSize: 12, color: tinted ? TOKENS.amberText : TOKENS.fg, fontWeight: 500 }}>
                {isBreak ? t('worktime.reconcile.break') : isOther ? t('worktime.reconcile.otherWork') : r.reg}
              </Box>
              <Box sx={{ fontSize: 12, color: TOKENS.muted, ...numFont }}>
                {hmShort((r.end - r.start) / 1000, t)}
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
